import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { CELL, GAP } from "./grid-constants";

/**
 * useDragSnap — owns pointer-drag math for desktop tiles.
 *
 * No store / geometry (placement) imports: it only knows the cell↔pixel
 * conversion (CELL/GAP layout constants) and reports the snapped (col,row)
 * back to the caller via `onDrop`. Collision / nearest-fit lives in the store.
 *
 * startDrag(id, e, originLeft, originTop):
 *   - originLeft/originTop are the tile's current top-left in grid pixels.
 *   - while pressed, `dragDelta` tracks pointer movement so the tile can render
 *     a live translate; on release we convert (origin + delta) → (col,row).
 *
 * A snapped cell is not enough to decide what a drop MEANS: the same (col,row)
 * comes out whether the pointer was over an empty patch of desktop or over the
 * dock. So the hook also reports the raw pointer position — `dragPoint` while
 * the drag is live, and a final `point` alongside (col,row) on drop. The hook
 * still has no opinion about what is at that position; the caller hit-tests.
 */
export type DragDelta = { dx: number; dy: number };
/** Viewport coordinates of the pointer, in the same space as DOMRect. */
export type DragPoint = { x: number; y: number };

type DragState = {
  id: string;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
};

const MOVE_THRESHOLD = 3; // px — below this a press is a click, not a drag

export function useDragSnap({
  onDrop,
}: {
  onDrop: (id: string, col: number, row: number, point: DragPoint) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragDelta, setDragDelta] = useState<DragDelta>({ dx: 0, dy: 0 });
  const [dragPoint, setDragPoint] = useState<DragPoint | null>(null);

  const state = useRef<DragState | null>(null);
  const moveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const upRef = useRef<((e: PointerEvent) => void) | null>(null);

  const cleanup = useCallback(() => {
    if (moveRef.current) window.removeEventListener("pointermove", moveRef.current);
    if (upRef.current) window.removeEventListener("pointerup", upRef.current);
    moveRef.current = null;
    upRef.current = null;
    state.current = null;
    setDragId(null);
    setDragDelta({ dx: 0, dy: 0 });
    setDragPoint(null);
  }, []);

  const startDrag = useCallback(
    (id: string, e: ReactPointerEvent, originLeft: number, originTop: number) => {
      state.current = { id, startX: e.clientX, startY: e.clientY, originLeft, originTop };
      setDragId(id);
      setDragDelta({ dx: 0, dy: 0 });
      setDragPoint({ x: e.clientX, y: e.clientY });

      const onMove = (ev: PointerEvent) => {
        const s = state.current;
        if (!s) return;
        setDragDelta({ dx: ev.clientX - s.startX, dy: ev.clientY - s.startY });
        setDragPoint({ x: ev.clientX, y: ev.clientY });
      };
      const onUp = (ev: PointerEvent) => {
        const s = state.current;
        cleanup();
        if (!s) return; // guard: pointerup with no active drag
        const dx = ev.clientX - s.startX;
        const dy = ev.clientY - s.startY;
        // A press without meaningful movement is a click (open/select), not a move.
        if (Math.abs(dx) < MOVE_THRESHOLD && Math.abs(dy) < MOVE_THRESHOLD) return;
        const col = Math.max(0, Math.round((s.originLeft + dx) / (CELL + GAP)));
        const row = Math.max(0, Math.round((s.originTop + dy) / (CELL + GAP)));
        onDrop(s.id, col, row, { x: ev.clientX, y: ev.clientY });
      };
      moveRef.current = onMove;
      upRef.current = onUp;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [cleanup, onDrop]
  );

  // Remove any dangling window listeners if the grid unmounts mid-drag.
  useEffect(() => cleanup, [cleanup]);

  return { dragId, dragDelta, dragPoint, startDrag };
}
